import {
  ORDER_STATUSES,
  REFUND_STATUSES,
  type LedgerOrderItem,
  type LedgerSummary,
  type OrderRefundResponse,
  type OrderStatus,
  type RefundRecord,
  type RefundStatus,
  type SoldInventoryItem,
} from '@cuito/types';
import { apiClient } from '../lib/apiClient';
import type { Paginated } from './catalog';

/**
 * 账本与退款的 API 封装（`04-api-contract.md` §13；主计划 §4.1 / §4.7 是字段名的唯一出处）。
 *
 * ## 视图类型**一律**从 `@cuito/types` re-export
 *
 * §13.2 ~ §13.7 的响应形状在 `packages/types/src/ledger.ts` 里已经有一份（Chain A Task 1），
 * 这里再抄一遍就会在字段增删时漂移 —— 而漂移的那一份不会有任何编译错误提示
 * （`ShopsPage.tsx` 的本地 `Shop` 接口就是这么被干掉的）。
 *
 * ## 两个端点是**裸数组**
 *
 * `sold-inventory`（§13.4）与 `orders/:id/refunds`（§13.7）都是裸数组，**不是** §1.3 的
 * 五字段分页包裹；`orders` 才是分页包裹。抄错形状的代价是运行时的 `undefined.map`。
 *
 * ## 本文件**不判**「这一行退完了没有」（契约 §13.8 的第二份判据）
 *
 * 行级文案（`未退款` / `部分退款` / `已全额退款`）的唯一实现是 `@cuito/types` 的
 * `refundLineStatusLabel(refundedQty, quantity, shortQty)`；订单级状态直接用**服务端算好**的
 * `refundStatus`。本文件只做透传 —— 在这里内联一句 `refundedQty === quantity`
 * （或 `>=`、或漏掉某一行）就是契约 §13.8 明令禁止的第二份判据：它在正常数据下与唯一实现
 * **逐字等价**，只有异常数据（历史超退、绕过端点直接改库）才分得出对错。
 * 因此 `ledger.spec.ts` 的静态门会**剥掉注释之后**再扫这条形态（本段注释就是它的活体对照：
 * 同一串写在注释里不算违规，写进代码面才算）。
 *
 * ## 金额一律字符串十进制透传
 *
 * §1.7：服务端下发的是字符串（两位小数），本层**不做** `Number()` / `toFixed` ——
 * 前端再算一次就会出现第二份金额口径（分位运算只在服务端与纯函数层做）。
 *
 * ## CSV 下载为什么用 `transformResponse`
 *
 * ① **直接读不到 `Content-Disposition`**：`ApiClient` 的适配器只回传 `res.data`
 *    （`packages/api-client/src/client.ts:65-72`），`get<T>` 拿不到响应头，
 *    而契约 §13.5 把文件名放在响应头里。
 * ② `transformResponse(data, headers)` 是 axios 在 `dispatchRequest` → `transformData`
 *    里调用的那一跳（`node_modules/axios/lib/core/dispatchRequest.js:63`），也是**唯一**
 *    能同时拿到 body 与响应头的位置。于是 `readCsvDownload` 把「字节 + 文件名」一起交出来，
 *    页面直接用服务端给的名字存盘（**不**在客户端拿当天日期重算一遍文件名）。
 *
 * 代价（控制器已接受，见草稿「待裁决」C-1）：错误响应也会经过这条链 ⇒ 导出失败时读不到
 * 错误体里的 `code`，页面一律显示 UI 规范 §3.2 的那句话（`导出失败，请重试`）。
 * 这条路径上不值得再解一次 blob。
 */

export type {
  ConsignmentAdjustment,
  LedgerOrderItem,
  LedgerOrderLine,
  LedgerSummary,
  OrderRefundResponse,
  RefundRecord,
  RefundRecordLine,
  RefundStatus,
  SoldInventoryItem,
} from '@cuito/types';

/** §13.1 四个账本端点的共同筛选（**不含分页**：导出与汇总都是全量的） */
export interface LedgerQuery {
  locationId?: string;
  /** ISO 8601；`Order.createdAt` 闭区间（`>= from`） */
  from?: string;
  /** ISO 8601；闭区间（`<= to`） */
  to?: string;
  keyword?: string;
  status?: OrderStatus;
  refundStatus?: RefundStatus;
}

/** `GET /ledger/orders` = 共同筛选 + §1.3 的分页 */
export interface LedgerOrdersQuery extends LedgerQuery {
  page?: number;
  pageSize?: number;
}

/**
 * 筛选表单的**串行化形状**：每个字段都是字符串，空串 = 不筛。
 *
 * 与契约查询分开的理由：`<input type="date">` 给的是 `2026-07-29`，而契约要的是
 * ISO 8601 的闭区间；`<select>` 给的是字符串，而契约要的是枚举。两种形状混在一个对象里，
 * 「空串」与「不传」就会在请求里变成两回事（日志、断言、服务端缓存键各说一套）。
 */
export interface LedgerFilterInput {
  locationId: string;
  /** 北京日 `YYYY-MM-DD`；空串 = 不限 */
  fromDay: string;
  toDay: string;
  keyword: string;
  /** `''` 或 `OrderStatus` */
  status: string;
  /** `''` 或 `RefundStatus` */
  refundStatus: string;
}

export const EMPTY_LEDGER_FILTERS: LedgerFilterInput = {
  locationId: '',
  fromDay: '',
  toDay: '',
  keyword: '',
  status: '',
  refundStatus: '',
};

/** 退款请求体（契约 §13.6 + 主计划 A3：字段是 `type` + `lines[].orderLineId`，不是 `scope` + `lineId`） */
export interface RefundRequest {
  type: 'FULL' | 'PARTIAL';
  reason: string;
  lines?: Array<{ orderLineId: string; quantity: number }>;
}

/** CSV 下载结果：**服务端原样下发的字节** + **服务端给的文件名** */
export interface LedgerCsvDownload {
  blob: Blob;
  filename: string;
}

/**
 * 响应头缺失时的兜底文件名。
 *
 * **不是**在客户端重算一遍服务端的命名规则（§4.7 的 `ledger-YYYYMMDD.csv` 由服务端算）：
 * 一个静态兜底名永远不会与日期说谎，而客户端自己拼日期会在跨时区/时钟偏移时与服务端分叉。
 */
export const CSV_FALLBACK_FILENAME = '账本.csv';

/** 北京的固定偏移（无夏令时）。写死它的唯一目的：让换算结果与运行机器的 `TZ` 无关 */
const BEIJING_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * 北京日的闭区间 → 契约 §13.1 的 `from` / `to`。
 *
 * `+08:00` 是**写死的**：北京无夏令时，写死偏移让结果与运行机器的 `TZ` 无关 ——
 * 用 `new Date('2026-07-29')` 那种解析会跟着机器时区漂移，测试也就跟着变成环境依赖。
 * ⚠️ 本机时区恰好是 `Asia/Shanghai`，所以「本地时区解析」的错误实现在**绝对值断言**下
 * 也可能全绿（`2026-07-29T00:00:00` 本地解析 = `2026-07-28T16:00:00.000Z`，与正确答案相同）
 * ⇒ 判据里必须有一条**切换 `process.env.TZ` 的对照**（见 `ledger.spec.ts` ⑩）。
 *
 * **日历回环校验是承重的**（实测）：`new Date('2026-02-30T00:00:00.000+08:00')` **不报 NaN** ——
 * JS 把不存在的日子**静默滚**到下一个月（`2026-02-30` ⇒ `2026-03-01T16:00:00.000Z`，
 * 即北京 3 月 2 日；`2026-04-31` ⇒ 北京 5 月 1 日）。只查 `Number.isNaN` 会把这些日子
 * 当成合法输入发出去：用户没输入过的日期被「猜」出来，服务端还会收到一个**语法合法**的
 * ISO 区间（`400 VALIDATION_FAILED` 拦不住它）。把 `from` 按固定偏移读回北京日再逐字比对，
 * 才是「这一天真的存在」的权威判据，而且只用 `+08:00` 与 UTC 运算 ⇒ 仍然与机器 `TZ` 无关。
 */
export function beijingDayRange(day: string): { from: string; to: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const from = new Date(`${day}T00:00:00.000+08:00`);
  const to = new Date(`${day}T23:59:59.999+08:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  // 回环：`from`（北京当日 00:00）加回偏移即北京当日 00:00Z，其日期前缀必须逐字等于输入
  const parsedDay = new Date(from.getTime() + BEIJING_OFFSET_MS).toISOString().slice(0, 10);
  if (parsedDay !== day) return null;
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * 筛选表单 → 契约查询参数。空串一律**丢弃**。
 *
 * 枚举用 `find` 而不是 `as OrderStatus`：`as` 会把用户篡改的 `<select>` 值带进请求，
 * 而服务端只会回一个 `400 VALIDATION_FAILED` —— 前端能做的是**不猜**（丢掉它），
 * 不是替服务端做校验（§13.1 的非法值口径仍由 DTO 负责）。
 */
export function ledgerQueryOf(filters: LedgerFilterInput): LedgerQuery {
  const query: LedgerQuery = {};
  if (filters.locationId !== '') query.locationId = filters.locationId;
  if (filters.keyword !== '') query.keyword = filters.keyword;

  const status = ORDER_STATUSES.find((value) => value === filters.status);
  if (status !== undefined) query.status = status;

  const refundStatus = REFUND_STATUSES.find((value) => value === filters.refundStatus);
  if (refundStatus !== undefined) query.refundStatus = refundStatus;

  const from = beijingDayRange(filters.fromDay);
  if (from !== null) query.from = from.from;
  const to = beijingDayRange(filters.toDay);
  if (to !== null) query.to = to.to;

  return query;
}

/** 只留基名：`Content-Disposition` 是服务端可控的字符串，路径会污染「下载到哪里」 */
function safeBasename(value: string): string | null {
  const name = (value.split(/[\\/]/).pop() ?? '').trim();
  if (name === '' || name === '.' || name === '..') return null;
  return name;
}

function decodeUriSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * 从 `Content-Disposition` 取文件名；取不到返回 `null`（调用方决定兜底）。
 *
 * 支持契约 §13.5 的形态（`attachment; filename="ledger-20260729.csv"`）、不带引号的形态，
 * 以及 RFC 6266 的 `filename*=UTF-8''%E8%B4%A6%E6%9C%AC.csv`（`filename*` 优先）。
 */
export function csvFilenameOf(disposition: unknown): string | null {
  if (typeof disposition !== 'string' || disposition.trim() === '') return null;

  const extended = /filename\*\s*=\s*([^;]+)/i.exec(disposition);
  if (extended?.[1] !== undefined) {
    const value = decodeUriSafe(extended[1].trim().replace(/^UTF-8''/i, ''));
    const name = safeBasename(value);
    if (name !== null) return name;
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(disposition);
  const bare = /filename\s*=\s*([^;]+)/i.exec(disposition);
  return safeBasename(quoted?.[1] ?? bare?.[1] ?? '');
}

/** 大小写不敏感地取一个响应头（真 axios 传进来的是 `AxiosHeaders`，键的大小写不保证） */
function headerValueOf(headers: unknown, name: string): string | null {
  if (typeof headers !== 'object' || headers === null) return null;
  const target = name.toLowerCase();
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    if (key.toLowerCase() === target && typeof value === 'string') return value;
  }
  return null;
}

/**
 * `transformResponse` 的实现：把「字节 + 文件名」一起交出来（见文件头注释 ①②）。
 *
 * 第二参数在真 axios 里是 `AxiosHeaders`（`core/transformData.js:19` 传的是
 * `headers.normalize()`），因此这里只当作 `unknown` 做大小写不敏感查找 ——
 * 不 import axios 的类型：axios 不是本应用的直接依赖（`lib/apiError.ts` 同款理由）。
 */
export function readCsvDownload(data: unknown, headers: unknown): LedgerCsvDownload {
  return {
    blob: data as Blob,
    filename: csvFilenameOf(headerValueOf(headers, 'content-disposition')) ?? CSV_FALLBACK_FILENAME,
  };
}

/**
 * 账本与退款的六个调用（契约 §13 端点表 1–6）。
 *
 * `refund` 的第三个参数是**幂等键**（契约 §1.5：「必填，不提供没有键也能退的降级路径」）。
 * 它由调用方（退款弹窗）生成并在重试时复用 —— 生成点放在这里就等于每次点击换一个键，
 * 「网络重试」会变成**退两次**。
 */
export const ledgerApi = {
  listOrders: (params: LedgerOrdersQuery = {}) =>
    apiClient.get<Paginated<LedgerOrderItem>>('/ledger/orders', { params }),

  summary: (params: LedgerQuery = {}) => apiClient.get<LedgerSummary>('/ledger/summary', { params }),

  /** **裸数组**（§13.4，与 `GET /stock/items` 同约定） */
  soldInventory: (params: LedgerQuery = {}) =>
    apiClient.get<SoldInventoryItem[]>('/ledger/sold-inventory', { params }),

  /**
   * CSV 导出（§13.5）。
   *
   * 为什么要 `transformResponse`（两行版，别删）：① **直接读不到 `Content-Disposition`** ——
   * `ApiClient` 的适配器只回传 `res.data`（`packages/api-client/src/client.ts:65-72`）；
   * ② `transformResponse` 是 axios 在 `dispatchRequest` → `transformData` 里调用的那一跳
   * （`node_modules/axios/lib/core/dispatchRequest.js:63`），也是**唯一**能同时拿到 body 与响应头的位置。
   *
   * 代价（控制器已接受，见草稿「待裁决」C-1）：错误响应也会过这条链 ⇒ 导出失败读不到错误体，
   * 页面一律显示 `导出失败，请重试`（UI 规范 §3.2 的文案）。
   * **不要**改成 `window.open(url)`：那条路径由浏览器重新发起请求，`Authorization` 头会丢（必然 401）。
   */
  exportCsv: (params: LedgerQuery = {}) =>
    apiClient.get<LedgerCsvDownload>('/ledger/export.csv', {
      params,
      responseType: 'blob',
      transformResponse: [readCsvDownload],
    }),

  refund: (orderId: string, body: RefundRequest, idempotencyKey: string) =>
    apiClient.post<OrderRefundResponse>(`/orders/${orderId}/refund`, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  /** **裸数组**，按 `createdAt` 降序（§13.7） */
  listRefunds: (orderId: string) => apiClient.get<RefundRecord[]>(`/orders/${orderId}/refunds`),
};
